import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { debounce } from 'throttle-debounce'
import { ExclamationCircleIcon } from '@heroicons/react/solid'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns-tz'
import Tippy from '@tippyjs/react'

import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import ToolTip from '@/shared/ToolTip'
import { toastSuccess, toastError } from '@/shared/Toast'
import { Modal } from '@/shared/Modal'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'

const startTimes = [ '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30', '04:00', '04:30',
  '05:00', '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30' ]

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// sourceCohortId is whichever cohort the events list is currently filtered to.
// Its sessions are what gets duplicated; the cohort picked in this form is
// where the copies land.
const DuplicateSessionsModal = ({ course, sourceCohortId, cohorts, isOpen, closeModal, onDuplicated }) => {
  const [, setToast] = useGlobalState('toast')
  const [timezones, setTimezones] = useState([])
  const [whenDays, setWhenDays] = useState(['Tue'])
  const [startDate, setStartDate] = useState(new Date())
  const [loading, setLoading] = useState(false)
  const { putpostRequest, getRequest } = useQuery()
  const { formState, register, unregister, handleSubmit, setValue, setError } = useForm({
    defaultValues: {
      start_date: new Date(),
      start_time: '09:00',
      timezone: 'America/New_York',
      duration: 60,
      ampm: 'AM'
    }
  })
  const { errors } = formState

  useEffect(() => {
    register('start_date', { required: true })
    register('when_days', { required: true })
    return () => {
      unregister('start_date')
      unregister('when_days')
    }
  }, [register])

  useEffect(() => {
    setValue('start_date', format(startDate, 'yyyy-MM-dd'), { shouldDirty: true, shouldValidate: true })
  }, [startDate])

  useEffect(() => {
    setValue('when_days', whenDays, { shouldDirty: true, shouldValidate: true })
  }, [whenDays])

  useEffect(() => {
    getRequest('/api/v1/static/timezones', {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }

      if (jsonData.timezones) {
        setTimezones(jsonData.timezones)
        setValue('timezone', 'America/New_York', { shouldDirty: false, shouldValidate: false })
      }
    })
  }, [])

  const updateWhenDays = (e, option) => {
    if (whenDays.includes(option)) {
      const oidx = whenDays.indexOf(option)
      whenDays.splice(oidx, 1)
      setWhenDays(whenDays.slice())
    } else {
      setWhenDays([...whenDays, option])
    }
  }

  const onSubmit = debounce(300, data => {
    setLoading(true)

    const formData = {
      course_id: course.id,
      source_cohort_id: sourceCohortId,
      ...data
    }

    putpostRequest('/api/v1/teachers/events/copy_series', 'POST', { series: formData }, (err, jsonData) => {
      setLoading(false)
      if (err) { // 422 code
        if (typeof err === 'string') {
          setToast(toastError(err))
        } else {
          Object.entries(err).forEach(([key, value]) => { setError(key, value) })
        }
        return
      }

      setToast(toastSuccess(`Duplicated ${jsonData.count} course sessions`))
      closeModal()
      if (onDuplicated) { onDuplicated(formData.cohort_id) }
    })
  })

  return (
    <Modal isOpen={isOpen} closeModal={closeModal}>
      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Duplicate All Course Sessions</h3>
      <div className='mt-2 text-sm text-gray-700 dark:text-gray-300'>
        Every session in the cohort you are filtered to gets copied into the cohort you pick below, as drafts, on a fresh schedule.
      </div>

      <form className='mt-6'>
        <div className="grid grid-cols-3 gap-6">

          <div className="col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
              <span>What days of the week?</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <div className='mt-2 text-xs text-gray-400 dark:text-gray-500'>One day means weekly. Pick two and the sessions alternate between them.</div>
            <div className="row mx-0 mt-6" data-cy="duplicate-days-of-week-input">
              <div className="grid w-full grid-cols-7 overflow-hidden rounded-lg border bg-white">
                { daysOfWeek.map((day) => (
                  <div key={`dupday${day}`} onClick={(e) => updateWhenDays(e, day)} className={`flex cursor-pointer items-center justify-center px-2 py-3 ${whenDays.includes(day) ? 'bg-clcnavy text-white' : 'hover:bg-gray-100 text-gray-dark'}`}><span className="align-middle text-sm font-medium">{day}</span></div>
                ))}
              </div>
            </div>
            { errors.when_days && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
          </div>

          <div className="col-span-3 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
              <span>When is the first event?</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <div className="mt-1 flex flex-col rounded-md shadow-sm">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={new Date().setMonth(startDate.getMonth() + 1)}
                minDate={new Date()}
                nextMonthButtonLabel=">"
                previousMonthButtonLabel="<"
              />
            </div>
          </div>

          <div className="col-span-3 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
              <span>Start Time</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <select
              {...register('start_time', { required: true })}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
            >
              { startTimes.map((time) => (
                <option key={`duptime${time}`} value={time}>{time}</option>
              ))}
            </select>
          </div>

          <div className="col-span-3 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
              <span>AM/PM</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <select
              {...register('ampm', { required: true })}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
            >
              <option>AM</option>
              <option>PM</option>
            </select>
          </div>

          <div className="col-span-3 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
              <span>Timezone</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <select
              {...register('timezone', { required: true })}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
            >
              { timezones.map((tz) => (
                <option key={`duptz${tz.computer}`} value={tz.computer}>{tz.human.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div className="col-span-3 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex justify-between">
              <span>Duration in Minutes</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <div className="mt-1 flex flex-col rounded-md shadow-sm">
              <div className='relative'>
                <input type="number" className={errors.duration ? 'errors' : ''} {...register('duration', { required: true })} />
                { errors.duration && <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                </div> }
              </div>
            </div>
            { errors.duration && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
          </div>

          <div className="col-span-3 sm:col-span-1"></div>

          <div className="col-span-3 sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 flex justify-between">
              <span>Cohort</span>
              <Tippy interactive={true} interactiveBorder={20} content={<ToolTip>Required Field</ToolTip>}>
                <span onClick={(e) => e.preventDefault()}><ExclamationCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" /></span>
              </Tippy>
            </label>
            <select
              {...register('cohort_id', { required: true })}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
            >
              <option value=''></option>
              { cohorts.map((cohort) => (
                <option key={`dupc${cohort.id}`} value={cohort.id}>{cohort.moniker}</option>
              ))}
            </select>
            { errors.cohort_id && <div className='mt-2 text-sm text-red-600'>This field is required.</div> }
          </div>
        </div>

        <div className='mt-6 flex space-x-3'>
          <PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} text="Duplicate Sessions" />
          <DefaultButton onClick={closeModal} text='Cancel' />
        </div>
      </form>
    </Modal>
  )
}

export default DuplicateSessionsModal
